import { useEffect, useState } from 'react';

import {
  ChakraBox,
  ChakraVStack,
  DateFormat,
  MaudSectionHeader,
} from '@equip.health/ui';
import { useParams } from 'react-router-dom';

import {
  ProfileEventsAdmin,
  ProfileEventsMaud,
} from '~/analytics/profileEvents';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import {
  PortalComponent,
  useComponentPortalContext,
} from '~/lib/contexts/ComponentPortalContext';
import { UserMode, useModeContext } from '~/lib/contexts/ModeContext';
import { useGetAllOutOfOfficeListQuery } from '~/store/services/profile';

import OutOfOfficeScheduleList from './OutOfOfficeScheduleList';
import { DateTime } from 'luxon';

const ProfileCalendar = () => {
  const params = useParams<UserProfileParams>();
  const providerExternalId = params.id ?? params[0].split('/')[0];

  const { data: outOfOfficeListData, isLoading } =
    useGetAllOutOfOfficeListQuery({
      providerExternalId,
      startDate: DateTime.now().toFormat(DateFormat.shortYearMonthDay),
    });
  const [showOOOAccordion, setShowOOOAccordion] = useState<boolean>(false);
  const { setComponentVisibility } = useComponentPortalContext();
  const [outOfOfficeData, setOutOfOfficeData] = useState<OOOAccordionData>();
  const { trackEvent } = useAnalytics();
  const { mode } = useModeContext();
  const isAdmin = mode === UserMode.ADMIN;

  useEffect(() => {
    if (showOOOAccordion) {
      setShowOOOAccordion(false);
      setComponentVisibility({
        component: PortalComponent.OUT_OF_OFFICE_ACCORDION,
        data: outOfOfficeData,
        isExpanded: true,
        isVisible: true,
      });
    }
  }, [outOfOfficeData, showOOOAccordion]);

  const handleAddOutOfOffice = () => {
    trackEvent(
      isAdmin
        ? ProfileEventsAdmin.adminAddOutOfOfficeButtonClicked
        : ProfileEventsMaud.maudAddOutOfOfficeButtonClicked,
      {
        source: isAdmin
          ? AnalyticsSourceValues.PROFILE_TAB_ADMIN
          : AnalyticsSourceValues.PROFILE_TAB_MAUD,
      },
    );
    const accordionData: OOOAccordionData = {
      outOfOfficeData: {
        startDate: DateTime.now().toFormat(DateFormat.shortYearMonthDay),
        endDate: DateTime.now().toFormat(DateFormat.shortYearMonthDay),
      },
      providerExternalId,
      type: 'Add',
    };
    setShowOOOAccordion(true);
    setOutOfOfficeData(accordionData);
  };

  const handleCardClick = (outOfOfficeId) => {
    trackEvent(
      isAdmin
        ? ProfileEventsAdmin.adminCalendarCardClicked
        : ProfileEventsMaud.maudCalendarCardClicked,
      {
        source: isAdmin
          ? AnalyticsSourceValues.PROFILE_TAB_ADMIN
          : AnalyticsSourceValues.PROFILE_TAB_MAUD,
      },
    );
    const outOfOfficeData = outOfOfficeListData.find(
      (item) => item.outOfOfficeId === outOfOfficeId,
    );

    const accordionData: OOOAccordionData = {
      outOfOfficeData,
      providerExternalId,
      type: 'View',
    };
    setShowOOOAccordion(true);
    setOutOfOfficeData(accordionData);
  };

  return (
    <ChakraBox maxHeight="80vh" overflowY="auto" padding="30px 24px 36px 0px">
      <ChakraVStack alignItems="normal" gap="24px" width="100%">
        <MaudSectionHeader
          id="out-of-office__list"
          onAdd={handleAddOutOfOffice}
          onAddTitle="Add out of office"
          style="secondary"
          title="Out of office"
        />
        <OutOfOfficeScheduleList
          isLoading={isLoading}
          listItems={outOfOfficeListData}
          onCardClick={handleCardClick}
        />
      </ChakraVStack>
    </ChakraBox>
  );
};

export default ProfileCalendar;
